import { getReceivables, getReceivableCustomers, getReceivedPayments } from '../services/api';
import { createKeyedCache } from '@redpanda/utils';
import { PMPayment } from '../types/pm';
import { create } from 'zustand';
import { persist } from 'zustand/middleware';

/** PAYMENT_OVERDUE 收款逾期／BILLABLE 可請款／INVOICED 已請款／OVERDUE 進度逾期（里程碑到期未完成）／UPCOMING 未到期 */
export type ReceivableStatus = 'PAYMENT_OVERDUE' | 'BILLABLE' | 'INVOICED' | 'OVERDUE' | 'UPCOMING';

/** 應收的一筆：依里程碑收款條件、沒有收款條件的分包（整包），或沒有分包的專案（整個專案） */
export interface Receivable {
  id: string;
  milestoneId: string | null;
  milestoneName: string | null;
  packageId: string | null;
  dueDate: string | null;
  completedDate: string | null;
  projectId: string;
  projectName: string;
  packageName: string | null;
  expected: number;
  received: number;
  outstanding: number;
  /** 請款日、預計收款日（沒請款為 null） */
  invoicedAt: string | null;
  paymentDueDate: string | null;
  overdueDays: number;
  status: ReceivableStatus;
}

interface ReceivableSummary {
  billable: { count: number; amount: number };
  invoiced: { count: number; amount: number };
  paymentOverdue: { count: number; amount: number };
  dueSoon: { count: number; amount: number };
}

export type AgingBucket = 'current' | 'days1to30' | 'days31to60' | 'days61to90' | 'over90';
export const AGING_BUCKETS: AgingBucket[] = ['current', 'days1to30', 'days31to60', 'days61to90', 'over90'];

/** 依客戶的一列：帳齡（已請款未收，依逾期天數分組）＋平均收款天數（歷史已收齊、有請款日的款項） */
export interface CustomerReceivable {
  /** null＝專案沒有關聯客戶 */
  customerId: string | null;
  aging: Record<AgingBucket, number>;
  invoicedOutstanding: number;
  paidCount: number;
  avgDaysToPay: number | null;
  /** 已收齊但沒有請款日、不列入平均天數的筆數 */
  excludedCount: number;
}

interface ReceivableState {
  items: Receivable[];
  summary: ReceivableSummary | null;
  /** 抓過一次（含 persist 還原）才算有資料；沒有時才顯示骨架 */
  lastFetchTime: number | null;
  customers: CustomerReceivable[];
  customersFetchTime: number | null;

  fetchReceivables: () => Promise<void>;
  fetchCustomers: () => Promise<void>;
}

// 照 pmDashboardStore：畫面先顯示上次的資料（persist），進頁面一律在背景重抓
export const useReceivableStore = create<ReceivableState>()(
  persist(
    (set) => ({
      items: [],
      summary: null,
      lastFetchTime: null,
      customers: [],
      customersFetchTime: null,

      fetchCustomers: async () => {
        try {
          const { data } = await getReceivableCustomers();
          set({ customers: data, customersFetchTime: Date.now() });
        } catch (error) {
          console.error('Failed to load customer receivables:', error);
          set(state => ({ customersFetchTime: state.customersFetchTime ?? Date.now() }));
        }
      },

      fetchReceivables: async () => {
        try {
          const { data } = await getReceivables();
          set({ items: data.items, summary: data.summary, lastFetchTime: Date.now() });
        } catch (error) {
          console.error('Failed to load receivables:', error);
          set(state => ({ lastFetchTime: state.lastFetchTime ?? Date.now() })); // 失敗也結束骨架，顯示空狀態
        }
      },
    }),
    {
      name: 'pm-receivable-storage',
      partialize: (state) => ({
        items: state.items,
        summary: state.summary,
        lastFetchTime: state.lastFetchTime,
        customers: state.customers,
        customersFetchTime: state.customersFetchTime,
      }),
    }
  )
);

/** 已收款的一筆：收款記錄附專案（含客戶）、里程碑、分包名稱 */
export type ReceivedPayment = PMPayment & {
  project: { id: string; name: string; customerId: string | null };
  milestone: { id: string; name: string } | null;
  package: { id: string; name: string } | null;
};

/** 已收款的一頁：記錄＋分頁＋整段期間的摘要（實收、收款差額、其中扣繳） */
export interface ReceivedPaymentsPage {
  data: ReceivedPayment[];
  pagination: { totalItems: number; totalPages: number; currentPage: number; pageSize: number };
  summary: { received: number; adjustments: number; withholding: number };
}

/** 已收款：依收款日區間＋頁數分開快取 */
export const useReceivedPaymentsCache = createKeyedCache(
  'pm-received-payments-storage',
  (query: { from?: string; to?: string; page: number }) => getReceivedPayments(query).then(r => r.data as ReceivedPaymentsPage)
);
